/**
 * Timestamp Utilities for transcript editing
 * Handles formatting, parsing, validation, and manipulation of word timestamps
 */

/**
 * Format seconds to MM:SS.mmm display format
 * @param {number} seconds - Time in seconds
 * @param {boolean} includeMs - Whether to include milliseconds (default: true)
 * @returns {string} Formatted time string
 */
export function formatTimestamp(seconds, includeMs = true) {
  if (seconds === null || seconds === undefined || isNaN(seconds)) {
    return includeMs ? '0:00.000' : '0:00';
  }

  const totalSeconds = Math.max(0, seconds);
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.floor(totalSeconds % 60);
  const ms = Math.round((totalSeconds % 1) * 1000);

  if (includeMs) {
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Parse user input to seconds
 * Supports formats: "1:23.456", "1:23", "83.456", "83"
 * @param {string} input - User input string
 * @returns {number|null} Time in seconds, or null if invalid
 */
export function parseTimestamp(input) {
  if (input === null || input === undefined) return null;

  const str = String(input).trim();
  if (!str) return null;

  // Try MM:SS.mmm or MM:SS format
  const colonMatch = str.match(/^(\d+):(\d{1,2})(?:\.(\d{1,3}))?$/);
  if (colonMatch) {
    const mins = parseInt(colonMatch[1], 10);
    const secs = parseInt(colonMatch[2], 10);
    const ms = colonMatch[3] ? parseInt(colonMatch[3].padEnd(3, '0'), 10) : 0;

    if (secs >= 60) return null; // Invalid seconds
    return mins * 60 + secs + ms / 1000;
  }

  // Try plain seconds (with optional decimal)
  const numMatch = str.match(/^(\d+)(?:\.(\d{1,3}))?$/);
  if (numMatch) {
    const secs = parseInt(numMatch[1], 10);
    const ms = numMatch[2] ? parseInt(numMatch[2].padEnd(3, '0'), 10) : 0;
    return secs + ms / 1000;
  }

  return null;
}

/**
 * Validate a word's timestamp
 * @param {object} word - Word object with start/end
 * @param {object} options - Validation options
 * @returns {object} { valid: boolean, errors: string[] }
 */
export function validateWordTimestamp(word, options = {}) {
  const errors = [];
  const { minDuration = 0.01, maxDuration = 60 } = options;

  if (word.start === undefined || word.start === null) {
    errors.push('Start time is required');
  }
  if (word.end === undefined || word.end === null) {
    errors.push('End time is required');
  }
  if (word.start < 0) {
    errors.push('Start time cannot be negative');
  }
  if (word.end < 0) {
    errors.push('End time cannot be negative');
  }
  if (word.start >= word.end) {
    errors.push('Start time must be before end time');
  }

  const duration = word.end - word.start;
  if (duration < minDuration) {
    errors.push(`Duration must be at least ${minDuration * 1000}ms`);
  }
  if (duration > maxDuration) {
    errors.push(`Duration exceeds maximum of ${maxDuration}s`);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Check for overlapping timestamps between words
 * @param {array} words - Array of word objects with start/end
 * @returns {array} Array of overlap objects { index1, index2, overlapAmount }
 */
export function detectTimestampOverlaps(words) {
  const overlaps = [];

  for (let i = 0; i < words.length - 1; i++) {
    const current = words[i];
    const next = words[i + 1];

    // Skip ghosted words
    if (current.isGhosted || next.isGhosted) continue;

    if (current.end > next.start) {
      overlaps.push({
        index1: i,
        index2: i + 1,
        overlapAmount: current.end - next.start
      });
    }
  }

  return overlaps;
}

/**
 * Detect gaps between words
 * @param {array} words - Array of word objects with start/end
 * @param {number} minGap - Minimum gap to report (default 0.1s)
 * @returns {array} Array of gap objects { index1, index2, gapAmount }
 */
export function detectTimestampGaps(words, minGap = 0.1) {
  const gaps = [];

  for (let i = 0; i < words.length - 1; i++) {
    const current = words[i];
    const next = words[i + 1];

    // Skip ghosted words
    if (current.isGhosted || next.isGhosted) continue;

    const gap = next.start - current.end;
    if (gap >= minGap) {
      gaps.push({
        index1: i,
        index2: i + 1,
        gapAmount: gap
      });
    }
  }

  return gaps;
}

/**
 * Ripple modes for timestamp adjustments
 */
export const RIPPLE_MODE = {
  ADJUST_NEIGHBOR: 'adjust_neighbor',
  ALLOW_GAP: 'allow_gap',
  SHIFT_ALL_BEFORE: 'shift_all_before',
  SHIFT_ALL_AFTER: 'shift_all_after'
};

/**
 * Apply timestamp change with ripple effect
 * @param {array} words - Array of word objects (will be mutated)
 * @param {number} wordIndex - Index of word being edited
 * @param {number} newStart - New start time
 * @param {number} newEnd - New end time
 * @param {string} rippleMode - One of RIPPLE_MODE values
 * @returns {object} { success: boolean, affectedIndices: number[] }
 */
export function applyTimestampWithRipple(words, wordIndex, newStart, newEnd, rippleMode = RIPPLE_MODE.ADJUST_NEIGHBOR) {
  const affectedIndices = [wordIndex];
  const word = words[wordIndex];
  const oldStart = word.start;
  const oldEnd = word.end;
  const startDelta = newStart - oldStart;
  const endDelta = newEnd - oldEnd;

  // Apply the change to the target word
  word.start = newStart;
  word.end = newEnd;

  switch (rippleMode) {
    case RIPPLE_MODE.ADJUST_NEIGHBOR:
      // Adjust previous word's end if start moved earlier
      if (startDelta < 0 && wordIndex > 0) {
        const prevWord = words[wordIndex - 1];
        if (!prevWord.isGhosted && prevWord.end > newStart) {
          prevWord.end = newStart;
          affectedIndices.push(wordIndex - 1);
        }
      }
      // Adjust previous word's end if start moved later (extend it)
      if (startDelta > 0 && wordIndex > 0) {
        const prevWord = words[wordIndex - 1];
        if (!prevWord.isGhosted) {
          prevWord.end = newStart;
          affectedIndices.push(wordIndex - 1);
        }
      }
      // Adjust next word's start if end moved later
      if (endDelta > 0 && wordIndex < words.length - 1) {
        const nextWord = words[wordIndex + 1];
        if (!nextWord.isGhosted && nextWord.start < newEnd) {
          nextWord.start = newEnd;
          affectedIndices.push(wordIndex + 1);
        }
      }
      // Adjust next word's start if end moved earlier (extend it)
      if (endDelta < 0 && wordIndex < words.length - 1) {
        const nextWord = words[wordIndex + 1];
        if (!nextWord.isGhosted) {
          nextWord.start = newEnd;
          affectedIndices.push(wordIndex + 1);
        }
      }
      break;

    case RIPPLE_MODE.ALLOW_GAP:
      // No ripple - just apply the change (gaps/overlaps allowed)
      break;

    case RIPPLE_MODE.SHIFT_ALL_BEFORE:
      // Shift all words before this one by the start delta
      if (startDelta !== 0) {
        for (let i = 0; i < wordIndex; i++) {
          if (!words[i].isGhosted) {
            words[i].start += startDelta;
            words[i].end += startDelta;
            affectedIndices.push(i);
          }
        }
      }
      break;

    case RIPPLE_MODE.SHIFT_ALL_AFTER:
      // Shift all words after this one by the end delta
      if (endDelta !== 0) {
        for (let i = wordIndex + 1; i < words.length; i++) {
          if (!words[i].isGhosted) {
            words[i].start += endDelta;
            words[i].end += endDelta;
            affectedIndices.push(i);
          }
        }
      }
      break;
  }

  return { success: true, affectedIndices: [...new Set(affectedIndices)] };
}

/**
 * Shift timestamps for a range of words
 * @param {array} words - Array of word objects (will be mutated)
 * @param {number} startIndex - Start index (inclusive)
 * @param {number} endIndex - End index (inclusive)
 * @param {number} offsetSeconds - Offset to apply (can be negative)
 * @returns {object} { success: boolean, affectedIndices: number[] }
 */
export function shiftTimestamps(words, startIndex, endIndex, offsetSeconds) {
  const affectedIndices = [];

  for (let i = startIndex; i <= endIndex && i < words.length; i++) {
    const word = words[i];
    if (!word.isGhosted) {
      word.start += offsetSeconds;
      word.end += offsetSeconds;

      // Clamp to non-negative
      if (word.start < 0) {
        word.end -= word.start;
        word.start = 0;
      }

      affectedIndices.push(i);
    }
  }

  return { success: true, affectedIndices };
}

/**
 * Distribute words evenly within a time range
 * @param {array} words - Array of word objects (will be mutated)
 * @param {number} startIndex - Start index (inclusive)
 * @param {number} endIndex - End index (inclusive)
 * @param {string} mode - 'even' or 'proportional' (by character count)
 * @returns {object} { success: boolean, affectedIndices: number[] }
 */
export function distributeTimestamps(words, startIndex, endIndex, mode = 'even') {
  const affectedIndices = [];
  const activeWords = [];

  // Collect non-ghosted words in range
  for (let i = startIndex; i <= endIndex && i < words.length; i++) {
    if (!words[i].isGhosted) {
      activeWords.push({ index: i, word: words[i] });
    }
  }

  if (activeWords.length < 2) {
    return { success: false, error: 'Need at least 2 words to distribute' };
  }

  const totalStart = activeWords[0].word.start;
  const totalEnd = activeWords[activeWords.length - 1].word.end;
  const totalDuration = totalEnd - totalStart;

  if (mode === 'even') {
    const durationPerWord = totalDuration / activeWords.length;
    let currentTime = totalStart;

    for (const { index, word } of activeWords) {
      word.start = currentTime;
      word.end = currentTime + durationPerWord;
      currentTime = word.end;
      affectedIndices.push(index);
    }
  } else if (mode === 'proportional') {
    // Calculate total character count
    const totalChars = activeWords.reduce((sum, { word }) => sum + (word.word?.length || 1), 0);
    let currentTime = totalStart;

    for (const { index, word } of activeWords) {
      const charCount = word.word?.length || 1;
      const proportion = charCount / totalChars;
      const duration = totalDuration * proportion;

      word.start = currentTime;
      word.end = currentTime + duration;
      currentTime = word.end;
      affectedIndices.push(index);
    }
  }

  return { success: true, affectedIndices };
}

/**
 * Stretch or compress timestamps by a scale factor
 * @param {array} words - Array of word objects (will be mutated)
 * @param {number} startIndex - Start index (inclusive)
 * @param {number} endIndex - End index (inclusive)
 * @param {number} scaleFactor - Scale factor (1.0 = no change, 2.0 = double duration)
 * @returns {object} { success: boolean, affectedIndices: number[] }
 */
export function stretchTimestamps(words, startIndex, endIndex, scaleFactor) {
  const affectedIndices = [];

  if (scaleFactor <= 0) {
    return { success: false, error: 'Scale factor must be positive' };
  }

  // Find the anchor point (first non-ghosted word's start)
  let anchorTime = null;
  for (let i = startIndex; i <= endIndex && i < words.length; i++) {
    if (!words[i].isGhosted) {
      anchorTime = words[i].start;
      break;
    }
  }

  if (anchorTime === null) {
    return { success: false, error: 'No active words in range' };
  }

  for (let i = startIndex; i <= endIndex && i < words.length; i++) {
    const word = words[i];
    if (!word.isGhosted) {
      const relativeStart = word.start - anchorTime;
      const relativeEnd = word.end - anchorTime;

      word.start = anchorTime + (relativeStart * scaleFactor);
      word.end = anchorTime + (relativeEnd * scaleFactor);
      affectedIndices.push(i);
    }
  }

  return { success: true, affectedIndices };
}

/**
 * Adjust boundary between two adjacent words
 * @param {array} words - Array of word objects (will be mutated)
 * @param {number} firstWordIndex - Index of first word
 * @param {number} newBoundary - New boundary time
 * @returns {object} { success: boolean, affectedIndices: number[] }
 */
export function adjustBoundary(words, firstWordIndex, newBoundary) {
  const firstWord = words[firstWordIndex];
  const secondWord = words[firstWordIndex + 1];

  if (!firstWord || !secondWord) {
    return { success: false, error: 'Invalid word indices' };
  }

  // Validate boundary is within reasonable range
  const minBoundary = firstWord.start + 0.01;
  const maxBoundary = secondWord.end - 0.01;

  if (newBoundary < minBoundary || newBoundary > maxBoundary) {
    return { success: false, error: 'Boundary out of valid range' };
  }

  firstWord.end = newBoundary;
  secondWord.start = newBoundary;

  return { success: true, affectedIndices: [firstWordIndex, firstWordIndex + 1] };
}

/**
 * Sync the flat words array with segments
 * @param {object} data - Transcript data with segments
 * @returns {object} Updated data with synced words array
 */
export function syncFlatWordsArray(data) {
  if (!data || !data.segments) return data;

  data.words = data.segments.flatMap(segment =>
    (segment.words || []).map(word => ({ ...word }))
  );

  return data;
}

/**
 * Update segment boundaries based on word timestamps
 * @param {object} segment - Segment object with words array
 * @returns {object} Updated segment with correct start/end
 */
export function updateSegmentBoundaries(segment) {
  if (!segment || !segment.words || segment.words.length === 0) {
    return segment;
  }

  const activeWords = segment.words.filter(w => !w.isGhosted);
  if (activeWords.length === 0) {
    return segment;
  }

  segment.start = Math.min(...activeWords.map(w => w.start));
  segment.end = Math.max(...activeWords.map(w => w.end));

  return segment;
}
